import loadable from '@loadable/component'
import {isRight} from 'fp-ts/lib/Either'
import React, {useEffect, useMemo, useState} from 'react'
import {Navigate, Routes, useLocation, useNavigate} from 'react-router-dom'
import {
  OrganisationPreferences,
  OrganisationPreferencesCodec
} from './common/types'
import {ErrorComponent, NoAccess, NotFound} from './components/errors'
import {LinkButton} from './components/library/buttons'
import {NoResults} from './components/library/list'
import {Loader} from './components/loader'
import {useUpdateCurrentAccountMutation} from './data/workbench-api/generated/account'
import {
  GetOrganisationQueryVariables,
  useGetOrganisationAccessLazyQuery
} from './data/workbench-api/generated/organisation'
import {
  OrganisationAccessEnvironment,
  PermissionAction,
  PermissionResource
} from './data/workbench-api/generated/types'
import {Environment, EnvironmentNode, EnvironmentRedirect} from './environment'
import {HeartbeatProvider} from './heartbeat'
import {useIdentifyBusinessUser} from './hooks/use-identify-business-user'
import {isMFAError} from './is-mfa-error'
import {getClient} from './lib/api'
import {UseAtomicSDK} from './hooks/atomic-sdk'
import {useAuth} from './lib/auth'
import {notify} from './lib/bugsnag'
import {AuthStateAuth} from './lib/cognito'
import {ERR_NO_ORGANISATIONS, getOrganisationId} from './lib/endpoints'
import {coerceString} from './lib/helpers'
import {setLocalOrganisationId} from './lib/local-store'
import {hasPermission, Permission} from './lib/permission'
import {Route} from './lib/router'
import {ActivityLog} from './views/activity-log'
import {EnvironmentList} from './views/environments'
import {OrgPreferences} from './views/org-preferences'
import {Permissions} from './views/permissions'
import {Profile} from './views/profile'

type RefetchOrg = (variables?: GetOrganisationQueryVariables) => void

export type AccessBusinessUser = {
  firstName: string
  lastName: string
  linkedTestUserId: string
}

export interface OrganisationNode {
  id: string
  name: string
  currentBusinessUser?: AccessBusinessUser
  preferences?: OrganisationPreferences

  environments: EnvironmentNode[]
  hasPermission: (
    resource: PermissionResource,
    action: PermissionAction,
    environmentId?: string
  ) => boolean
  refetch?: RefetchOrg
}

export interface OrganisationData {
  organisationId: string
  organisation?: OrganisationNode
  loading: boolean
  missing: boolean
  mfaRequired: boolean
  error?: Error
  refetch?: RefetchOrg
}

const AuditLog = loadable(
  async () => (await import('./views/audit-log')).AuditLog,
  {
    fallback: <Loader />
  }
)

// Note: this hook should only be used once, at the top level. Use useEnvironment
// to retrieve the current organisation details where needed
export const useOrganisation = (organisationId: string): OrganisationData => {
  const [missing, setMissing] = useState(false)
  const [mfaRequired, setMfaRequired] = useState(false)

  // call once on init
  const [updateUserAuth] = useUpdateCurrentAccountMutation({
    client: getClient(organisationId),
    refetchQueries: ['GetOrganisationAccess'],
    variables: {organisationId}
  })
  useEffect(() => {
    updateUserAuth()
  }, [updateUserAuth])

  const [doOrgQuery, {data, error, refetch, loading}] =
    useGetOrganisationAccessLazyQuery({
      onCompleted: res => {
        if (res.getOrganisationAccess) {
          setLocalOrganisationId(res.getOrganisationAccess.id)
          setMissing(false)
        } else {
          setMissing(true)
        }
        setMfaRequired(false)
      },
      onError: e => {
        if (isMFAError(e)) {
          setMfaRequired(true)
        }
      },

      // Note, the query func doesn't accept a client arg, so need to set here
      client: getClient(organisationId)
    })

  useEffect(() => {
    doOrgQuery({variables: {id: organisationId}})
  }, [doOrgQuery, organisationId])

  const organisation: OrganisationNode | undefined = useMemo(() => {
    const org = data?.getOrganisationAccess

    if (org) {
      const permissions =
        org.permissions?.filter((p): p is Permission => !!p) ?? []

      const hasOrgPermission = (
        resource: PermissionResource,
        action: PermissionAction,
        environmentId?: string
      ) => hasPermission(permissions, resource, action, environmentId)

      const preferencesDecode = OrganisationPreferencesCodec.decode(
        org.preferences
      )

      return {
        id: org.id,
        name: org.name,
        currentBusinessUser:
          // if the object is returned at all, it will be valid, but graphile
          // insists on making everything nullable
          (org.currentBusinessUser as AccessBusinessUser) ?? undefined,
        refetch,
        environments:
          org.environments
            ?.filter((e): e is OrganisationAccessEnvironment => !!e) // env might be null
            .map(env => {
              const cache = new Map<string, boolean>()
              return {
                ...env,

                // shortcuts
                organisationId: org.id,
                organisationName: org.name,

                hasPermission: (
                  resource: PermissionResource,
                  action: PermissionAction
                ) => {
                  const key = `${resource}:${action}`
                  if (cache.has(key)) {
                    return Boolean(cache.get(key))
                  }
                  const value = hasOrgPermission(resource, action, env.id)
                  cache.set(key, value)
                  return value
                }
              }
            }) ?? [],
        hasPermission: hasOrgPermission,
        preferences: isRight(preferencesDecode)
          ? preferencesDecode.right
          : undefined
      }
    }
    return undefined
  }, [data?.getOrganisationAccess, refetch])

  const response: OrganisationData = useMemo(
    () => ({
      organisationId, // represents the id present in the URL, which we may have not been able to fetch
      // only provide the loaded org obj if it matches
      organisation:
        organisation?.id === organisationId ? organisation : undefined,
      loading,
      missing,
      mfaRequired,
      error,
      refetch
    }),
    [
      error,
      loading,
      mfaRequired,
      missing,
      organisation,
      organisationId,
      refetch
    ]
  )

  return response
}

export const Organisation = ({
  organisationData,
  authState,
  atomicSDK
}: {
  organisationData?: OrganisationData
  authState: AuthStateAuth
  atomicSDK: UseAtomicSDK
}) => {
  const {organisationId, organisation, loading, error, missing, mfaRequired} =
    organisationData ?? {}

  // this is just somewhere useful to call this after login
  const userData = useIdentifyBusinessUser(organisationId)

  useEffect(() => {
    const businessUser = userData?.getOrganisationAccess?.currentBusinessUser
    // NOTE: if user info differs between orgs then the most-recently visited will overwrite
    if (businessUser)
      atomicSDK.updateUserProfile({
        name: [
          coerceString(businessUser.firstName),
          coerceString(businessUser.lastName)
        ]
          .filter(x => !!x)
          .join(' '),
        phone: coerceString(businessUser.phone)
      })
  }, [atomicSDK, userData?.getOrganisationAccess?.currentBusinessUser])

  if (!authState.mfaEnabled && !authState.ssoLogin && mfaRequired) {
    return (
      <ErrorComponent
        title="Multi-factor authentication required"
        error={
          <>
            <p>
              This organisation requires multi-factor authentication to access.
            </p>
            <LinkButton
              style={{
                marginTop: '10px',
                maxWidth: '160px'
              }}
              size="small"
              to={`/mfa-setup?redirect=/${organisationId}&cancel=/${organisationId}`}
              iconLeft="key"
              appearance="secondary"
              stretch="center"
            >
              Enable MFA
            </LinkButton>
          </>
        }
      />
    )
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (missing) {
    return <NotFound />
  }

  return organisation ? (
    <HeartbeatProvider>
      <Routes>
        <Route
          path="/"
          render={() => (
            <EnvironmentRedirect
              organisation={organisation}
              loaded={!loading}
            />
          )}
        />
        <Route
          path="/cards"
          render={() => (
            <EnvironmentRedirect
              organisation={organisation}
              loaded={!loading}
              subPath={'cards'}
            />
          )}
        />
        <Route
          path="/action-flows"
          render={() => (
            <EnvironmentRedirect
              organisation={organisation}
              loaded={!loading}
              subPath={'action-flows'}
            />
          )}
        />
        <Route
          path="/:environmentId/*"
          render={({environmentId}) => (
            <Environment environmentId={environmentId} loaded={!loading} />
          )}
        />
        <Route
          path="/environments"
          render={() => <EnvironmentList organisation={organisation} />}
        />
        <Route
          path="/activity-log"
          render={() => <ActivityLog organisation={organisation} />}
        />
        <Route
          path="/audit-log"
          render={() => <AuditLog organisation={organisation} />}
        />
        <>
          <Route
            path="/preferences/*"
            render={() => <OrgPreferences organisation={organisation} />}
          />
          <Route
            path="/profile/*"
            render={() => <Profile organisation={organisation} />}
          />
        </>
        <Route
          path="/users/*"
          render={() => <Navigate to={`/${organisation.id}/permissions`} />}
        />
        <Route
          path="/permissions/*"
          render={() => <Permissions organisation={organisation} />}
        />
      </Routes>
    </HeartbeatProvider>
  ) : (
    <Loader />
  )
}

export const OrganisationRedirect = ({subPath}: {subPath?: string}) => {
  const {auth, authState} = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<Error>()
  const [loaded, setLoaded] = useState<boolean>(false)
  const location = useLocation()

  useEffect(() => {
    const appClientId = auth.getClientId()

    getOrganisationId(appClientId)
      .then(id => {
        const to = subPath ? `/${id}/${subPath}${location.search}` : id
        navigate(to, {replace: true})
      })
      .catch(e => {
        if (e.message !== ERR_NO_ORGANISATIONS) {
          setError(e)
        }
        // otherwise user has created an account but has
        // not selected an organisation yet, this is
        // not an error, our state will transition to
        // loading false, and organisation undefined
        setLoaded(true)
      })
  }, [auth, location.search, navigate, subPath])

  if (error) {
    return <ErrorComponent error={error} />
  }
  if (loaded) {
    // Not something that should happen in production
    // I see this when reseting the dev database after inviting a user
    // this will happen in production if a user goes direct to workbench.atomic.io
    // after logging in via a new auth provider, i.e. SSO
    notify(new Error('No organisations available for authenticated user'), {
      authState
    })
    return (
      <NoAccess>
        <NoResults heading="No active organisations" icon="plug" message={''}>
          <p>Please contact your organisation administrator</p>
        </NoResults>
      </NoAccess>
    )
  }
  return <Loader />
}
